import { ERROR_TYPE } from '@/features/game-window-integration/helpers/error-mapping';
import {
  ProviderAdaptor,
  ReactiveOutboundMessage,
} from '@/features/game-window-integration/providers/generic-provider/payload-adaptors/contracts';
import { runProcessError } from '@/features/game-window-integration/providers/generic-provider/payload-adaptors/process-error';
import {
  ExtendableGenericEvents,
  GenericEvents,
  GenericEventsData,
} from '@/features/game-window-integration/providers/generic-provider/types';

type FspInboundPayload = {
  name?: string;
  updatedGameStatus?: string;
  percentage?: number;
  winAmount?: number;
  stakeAmount?: number;
  operatorErrorPayload?: Record<string, unknown>;
  newOperatorContext?: Record<string, unknown>;
  payload?: {
    data?: {
      soundEnabled?: boolean;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const eventsRemap: Partial<
  Record<ExtendableGenericEvents, ExtendableGenericEvents>
> = {
  [GenericEvents.GAME_LOAD_PROGRESS]: 'LoadProgress',
  [GenericEvents.APPLICATION_READY]: 'gameReadyForInteraction',
  [GenericEvents.AUTOPLAY_STOP]: 'autoPlayStopped',
  [GenericEvents.SPIN_IN_PROGRESS]: 'gameActive',
  [GenericEvents.SPIN_IN_PROGRESS_ENDED]: 'gameAnimationCompleted',
  [GenericEvents.RELOAD_BALANCE]: 'operatorTotalBalanceUpdated',
  [GenericEvents.BACK_TO_PARTNER]: 'exitGame',
  [GenericEvents.GAME_LOAD_END]: 'LoadCompleted',
  // SOUND_OFF and SOUND_ON are intentionally not remapped;
  // they are resolved by the inbound adaptor via settingsUpdated detection.
  [GenericEvents.ERROR_DELEGATION]: 'gameError',
};

/**
 * Maps outbound events (after being renamed by `eventsRemap`) to the FSP
 * `PublishEvent` `eventData` the game expects. Anything not listed is
 * treated as unsupported and skipped.
 */
const outboundPublishEventData: Record<
  string,
  (payload?: unknown) => Record<string, unknown>
> = {
  [eventsRemap[GenericEvents.RELOAD_BALANCE] as string]: (payload) => ({
    name: 'operatorTotalBalanceUpdated',
    balance: payload,
  }),
  [eventsRemap[GenericEvents.AUTOPLAY_STOP] as string]: () => ({
    name: 'stopAutoPlay',
  }),
  [GenericEvents.TOGGLE_SOUND]: () => ({ name: 'toggleSound' }),
  [GenericEvents.TOGGLE_PAYTABLE]: () => ({ name: 'openGamePaytable' }),
  [GenericEvents.TOGGLE_HELP]: () => ({ name: 'openGameInfo' }),
};

const buildFspEnvelope = (
  messageType: string,
  messageName: string,
  payload: unknown,
): GenericEventsData =>
  ({
    messageId: crypto.randomUUID(),
    version: '1',
    messageType,
    messageName,
    payload,
  }) as GenericEventsData;

const resolveFspInboundType = (
  providerData: GenericEventsData,
  payload: FspInboundPayload | undefined,
  isSoundSettingsUpdated: boolean,
  soundEnabled: boolean | undefined,
): string | undefined => {
  if (providerData.eventName) return providerData.eventName;
  if (payload?.name === 'gameStatusUpdated') {
    return payload.updatedGameStatus || payload.name || providerData.type;
  }
  if (isSoundSettingsUpdated) {
    return soundEnabled ? GenericEvents.SOUND_ON : GenericEvents.SOUND_OFF;
  }
  return payload?.name || providerData.type;
};

const adaptFspInboundPayload = (
  providerData: GenericEventsData,
  payload: FspInboundPayload | undefined,
  isSoundSettingsUpdated: boolean,
  soundEnabled: boolean | undefined,
): FspInboundPayload | undefined => {
  if (
    providerData.eventName === 'LoadProgress' &&
    typeof payload?.percentage === 'number'
  ) {
    return { ...payload, progress: payload.percentage };
  }
  if (isSoundSettingsUpdated) {
    return { ...payload, mute: !soundEnabled };
  }
  if (payload?.name === 'winUpdate') {
    return { ...payload, winAmount: payload.winAmount };
  }
  if (payload?.name === 'stakeUpdated') {
    return { ...payload, stakeAmount: payload.stakeAmount };
  }
  return payload;
};

export const flutterStudiosAdaptor: ProviderAdaptor = {
  eventsRemap,

  errorCodesRemap: {
    GAME_ERROR: ERROR_TYPE.GENERIC,
    402: ERROR_TYPE.INSUFFICIENT_FUNDS,
    SESSION_EXPIRED: ERROR_TYPE.SESSION_EXPIRED,
    CONNECTION_ERROR: ERROR_TYPE.CONNECTION_LOST,
    failedToReconnectToWsServer: ERROR_TYPE.CONNECTION_LOST,
    5: ERROR_TYPE.SESSION_EXPIRED,
    500: ERROR_TYPE.SESSION_EXPIRED,
  },

  errorMessages: {
    GAME_ERROR: 'A game error occurred',
    SESSION_SUPERSEDED: 'The session is invalid/expired',
    INTERNAL: 'The session is invalid/expired',
  },

  adaptInbound(providerData) {
    const payload = providerData.payload as FspInboundPayload | undefined;
    const soundEnabled = payload?.payload?.data?.soundEnabled;
    const isSoundSettingsUpdated =
      payload?.name === 'settingsUpdated' && typeof soundEnabled === 'boolean';

    const type = resolveFspInboundType(
      providerData,
      payload,
      isSoundSettingsUpdated,
      soundEnabled,
    );
    const adaptedPayload = adaptFspInboundPayload(
      providerData,
      payload,
      isSoundSettingsUpdated,
      soundEnabled,
    );

    if (providerData.eventType === 'Error' && payload) {
      return {
        type: 'gameError',
        payload: adaptedPayload,
      };
    }

    return {
      ...providerData,
      type,
      eventId: providerData.eventId,
      payload: adaptedPayload,
    };
  },

  adaptOutbound(eventToProvider, _notify, payload) {
    const fspPayload = payload as Record<string, unknown> | undefined;

    if (eventToProvider === 'Subscribe') {
      return buildFspEnvelope('Event', 'Subscribe', {
        eventType: fspPayload?.eventType,
      });
    }

    if (eventToProvider === 'Acknowledge') {
      return buildFspEnvelope('Event', 'Acknowledge', {
        eventId: fspPayload?.eventId,
        removeFromQueue: fspPayload?.removeFromQueue,
      });
    }

    if (eventToProvider === 'Lifecycle') {
      return buildFspEnvelope(
        'Lifecycle',
        fspPayload?.messageName as string,
        fspPayload?.payload,
      );
    }

    const eventData = outboundPublishEventData[eventToProvider]?.(payload);
    if (!eventData) return undefined;

    return buildFspEnvelope('Event', 'PublishEvent', {
      event: {
        originator: 'Operator',
        eventType: 'Operator',
        eventData,
      },
    });
  },

  getReactiveOutbound(providerData) {
    const messages: ReactiveOutboundMessage[] = [];

    if (
      providerData.eventType === 'Lifecycle' &&
      providerData.eventName === 'brandAdapterIsReady'
    ) {
      ['Game', 'Error'].forEach((v) => {
        messages.push({
          event: 'Subscribe',
          payload: { eventType: v },
        });
      });
    }

    if (providerData.eventId) {
      const payload = providerData.payload as FspInboundPayload | undefined;
      const removeFromQueue =
        payload?.name === 'gameStatusUpdated' ? 'no' : 'yes';
      messages.push({
        event: 'Acknowledge',
        payload: {
          eventId: providerData.eventId,
          removeFromQueue,
        },
      });
    }

    return messages;
  },

  processError(error) {
    return runProcessError(error, this.errorCodesRemap, this.errorMessages);
  },
};
